import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaRocket,
  FaLifeRing,
  FaServer,
  FaNetworkWired,
  FaShieldAlt,
  FaDatabase,
  FaHeartbeat,
  FaHeadset,
  FaProjectDiagram,
  FaCheckCircle,
  FaUsers,
  FaClock,
  FaBolt,
  FaAward,
  FaPhoneAlt,
  FaLaptopCode,
  FaUserTie,
  FaWrench,
  FaGraduationCap,
  FaHandsHelping,
} from 'react-icons/fa';
import { useScrollAnimation } from '../../hooks';
import { useLanguage } from '../../contexts/LanguageContext'; // Import du hook
import { Helmet } from 'react-helmet-async';

const DeploiementSupport = () => {
  const navigate = useNavigate();
  const { t } = useLanguage(); // Utilisation des traductions
  
  // Animation hooks
  const [setBannerRef, bannerInView] = useScrollAnimation(0.1);
  const [setDeploiementRef, deploiementInView] = useScrollAnimation(0.1);
  const [setSupportRef, supportInView] = useScrollAnimation(0.1);
  const [setProcessRef, processInView] = useScrollAnimation(0.1);
  const [setCtaRef, ctaInView] = useScrollAnimation(0.1);

  // Banner images carousel
  const bannerImages = [
    "/images/datacenter.webp",
    "/images/server1.jpg",
    "/images/network.avif",
    "/images/security.webp"
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % bannerImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [bannerImages.length]);

  // Map des icônes pour le déploiement
  const deploiementIcons = [<FaServer />, <FaNetworkWired />, <FaDatabase />, <FaShieldAlt />];
  const deploiementColors = ["#0ea5e9", "#059669", "#dc2626", "#7c3aed"];
  const deploiementImages = ["/images/server1.jpg", "/images/network.avif", "/images/datacenter.webp", "/images/security.webp"];

  // Map des icônes pour le support
  const supportIcons = [<FaHeadset />, <FaHeartbeat />, <FaWrench />, <FaGraduationCap />];
  const supportColors = ["#f59e42", "#059669", "#dc2626", "#7c3aed"];

  // Map des icônes pour le processus
  const processIcons = [<FaProjectDiagram />, <FaLaptopCode />, <FaRocket />, <FaCheckCircle />, <FaGraduationCap />, <FaLifeRing />];

  // Map des icônes pour les avantages
  const advantageIcons = [<FaAward />, <FaBolt />, <FaUsers />, <FaShieldAlt />];
  const advantageColors = ["#f59e42", "#dc2626", "#059669", "#7c3aed"];

  return (
    <div>

      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.deploiementSupport.seo.title}</title>
        <meta name="description" content={t.deploiementSupport.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/services/deploiement-support" />
        
        {/* Open Graph (Réseaux Sociaux) */}
        <meta property="og:title" content={t.deploiementSupport.seo.title} />
        <meta property="og:description" content={t.deploiementSupport.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/server1.jpg" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* ---------------------------------- */}

      {/* Banner Section */}
      <section 
        ref={setBannerRef}
        style={{ 
          position: 'relative',
          height: '75vh',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <div style={{ position: 'absolute', inset: 0 }}>
          {bannerImages.map((image, index) => (
            <div
              key={index}
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url(${image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: index === currentImageIndex ? 1 : 0,
                transition: 'opacity 1.5s ease-in-out'
              }}
            />
          ))}
        </div>

        <Container style={{ position: 'relative', zIndex: 2 }}>
          <Row className="justify-content-center text-center">
            <Col lg={10}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={bannerInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 1 }}
              >
                <motion.h1 
                  style={{ color: '#fff', fontSize: '3.8rem', fontWeight: 800, marginBottom: '1.5rem', textShadow: '2px 2px 8px rgba(0,0,0,0.7)' }}
                  dangerouslySetInnerHTML={{ __html: t.deploiementSupport.banner.title }}
                />
                <motion.p 
                  style={{ color: 'rgba(255,255,255,0.95)', fontSize: '1.5rem', fontWeight: 500, marginBottom: '2.5rem', textShadow: '1px 1px 4px rgba(0,0,0,0.7)', lineHeight: 1.4 }}
                >
                  {t.deploiementSupport.banner.subtitle}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={bannerInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="d-flex flex-wrap justify-content-center gap-3"
                >
                  <Button
                    style={{
                      background: 'linear-gradient(135deg, #d90000, #ff4444)',
                      border: 'none', borderRadius: '25px', padding: '15px 35px', fontSize: '1.1rem', fontWeight: 600, boxShadow: '0 6px 20px rgba(217,0,0,0.4)', transition: 'all 0.3s ease'
                    }}
                    onClick={() => navigate('/#contact')}
                  >
                    <FaRocket style={{ marginRight: '10px' }} />
                    {t.deploiementSupport.banner.cta}
                  </Button>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Déploiement Section */}
      <section ref={setDeploiementRef} id="deploiement" style={{ padding: '6rem 0', background: '#f8fafc' }}>
        <Container>
          <Row>
            <Col lg={12} className="text-center mb-5">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={deploiementInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
                <Badge style={{ background: 'linear-gradient(135deg, #d90000, #ff4444)', fontSize: '0.9rem', padding: '8px 20px', marginBottom: '1rem' }}>
                  <FaRocket className="me-2" />
                  {t.deploiementSupport.deploiement.badge}
                </Badge>
                <h2 style={{ fontSize: '3rem', fontWeight: 700, color: '#1e293b', marginBottom: '1.5rem', lineHeight: 1.2 }}
                    dangerouslySetInnerHTML={{ __html: t.deploiementSupport.deploiement.title }} />
                <p style={{ fontSize: '1.3rem', color: '#64748b', maxWidth: '800px', margin: '0 auto', lineHeight: 1.6 }}>
                  {t.deploiementSupport.deploiement.description}
                </p>
              </motion.div>
            </Col>
          </Row>

          <Row className="g-4">
            {t.deploiementSupport.deploiement.services.map((service, index) => (
              <Col lg={6} key={index}>
                <motion.div initial={{ opacity: 0, y: 50 }} animate={deploiementInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: index * 0.1 }} whileHover={{ y: -8, transition: { duration: 0.3 } }} style={{ height: '100%' }}>
                  <Card style={{ border: 'none', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', height: '100%', background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}>
                    <div style={{ height: '200px', backgroundImage: `linear-gradient(135deg, ${deploiementColors[index]}dd, ${deploiementColors[index]}), url(${deploiementImages[index]})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ color: 'white', fontSize: '3.5rem', textShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>{deploiementIcons[index]}</div>
                    </div>
                    <Card.Body style={{ padding: '2.5rem' }}>
                      <h3 style={{ color: deploiementColors[index], fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>{service.title}</h3>
                      <p style={{ color: '#64748b', lineHeight: 1.6, marginBottom: '1.5rem', fontSize: '1.05rem' }}>{service.desc}</p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {service.features.map((feature, idx) => (
                          <li key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.8rem' }}>
                            <FaCheckCircle style={{ color: deploiementColors[index], marginRight: '0.8rem', fontSize: '0.9rem' }} />
                            <span style={{ color: '#475569', fontSize: '0.95rem' }}>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>

          <Row className="mt-6">
            <Col lg={12} className="text-center mb-5">
              <motion.h3 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#1e293b', marginBottom: '1rem', marginTop: '2rem' }}>
                {t.deploiementSupport.deploiement.processTitle}
              </motion.h3>
              <motion.p style={{ fontSize: '1.2rem', color: '#64748b' }}>
                {t.deploiementSupport.deploiement.processSub}
              </motion.p>
            </Col>
          </Row>

          <Row className="g-4">
            {t.deploiementSupport.deploiement.steps.map((step, index) => (
              <Col lg={4} md={6} key={index}>
                <motion.div initial={{ opacity: 0, y: 50 }} animate={deploiementInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                  style={{ background: '#fff', borderRadius: '20px', padding: '2rem 1.5rem', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', position: 'relative', height: '100%', border: '1px solid #e2e8f0' }}>
                  <div style={{ position: 'absolute', top: '-20px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #d90000, #ff4444)', color: 'white', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: 700, boxShadow: '0 4px 15px rgba(217,0,0,0.3)' }}>
                    {step.step}
                  </div>
                  <div style={{ marginTop: '1.5rem' }}>
                    <div style={{ color: '#d90000', fontSize: '2.5rem', marginBottom: '1rem' }}>{processIcons[index]}</div>
                    <h4 style={{ color: '#1e293b', fontWeight: 700, marginBottom: '0.8rem', fontSize: '1.2rem' }}>{step.title}</h4>
                    <p style={{ color: '#64748b', marginBottom: '1rem', lineHeight: 1.5, fontSize: '0.95rem' }}>{step.desc}</p>
                    <Badge style={{ background: 'linear-gradient(135deg, #0ea5e9, #3b82f6)', padding: '6px 12px', fontSize: '0.8rem' }}>
                      <FaClock className="me-1" /> {step.timeline}
                    </Badge>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Support Section */}
      <section ref={setSupportRef} id="support" style={{ padding: '6rem 0', background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' }}>
        <Container>
          <Row>
            <Col lg={12} className="text-center mb-5">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={supportInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
                <Badge style={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.9rem', padding: '8px 20px', marginBottom: '1rem', border: '1px solid rgba(255,255,255,0.3)' }}>
                  <FaLifeRing className="me-2" /> {t.deploiementSupport.support.badge}
                </Badge>
                <h2 style={{ fontSize: '3rem', fontWeight: 700, color: '#ffffff', marginBottom: '1.5rem', lineHeight: 1.2, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}
                    dangerouslySetInnerHTML={{ __html: t.deploiementSupport.support.title }} />
                <p style={{ fontSize: '1.3rem', color: 'rgba(255,255,255,0.9)', maxWidth: '800px', margin: '0 auto', lineHeight: 1.6 }}>
                  {t.deploiementSupport.support.description}
                </p>
              </motion.div>
            </Col>
          </Row>

          <Row className="g-4">
            {t.deploiementSupport.support.services.map((service, index) => (
              <Col lg={6} key={index}>
                <motion.div initial={{ opacity: 0, y: 50 }} animate={supportInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: index * 0.1 }} whileHover={{ y: -8, transition: { duration: 0.3 } }} style={{ height: '100%' }}>
                  <Card style={{ border: 'none', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', height: '100%', background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}>
                    <Card.Body style={{ padding: '2.5rem' }}>
                      <div style={{ background: `linear-gradient(135deg, ${supportColors[index]}, ${supportColors[index]}dd)`, color: 'white', width: '80px', height: '80px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem', fontSize: '2.2rem' }}>
                        {supportIcons[index]}
                      </div>
                      <h3 style={{ color: '#1e293b', fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>{service.title}</h3>
                      <p style={{ color: '#64748b', lineHeight: 1.6, marginBottom: '1.5rem', fontSize: '1.05rem' }}>{service.desc}</p>
                      <ul style={{ listStyle: 'none', padding: 0, marginBottom: '1.5rem' }}>
                        {service.features.map((feature, idx) => (
                          <li key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.8rem' }}>
                            <FaCheckCircle style={{ color: supportColors[index], marginRight: '0.8rem', fontSize: '0.9rem' }} />
                            <span style={{ color: '#475569', fontSize: '0.95rem' }}>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {service.stats && (
                        <div style={{ background: '#f1f5f9', borderRadius: '12px', padding: '1rem' }}>
                          <Row className="text-center">
                            {Object.entries(service.stats).map(([key, value], idx) => (
                              <Col key={idx} xs={4}>
                                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: supportColors[index] }}>{value}</div>
                                <div style={{ fontSize: '0.8rem', color: '#64748b', textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}</div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>

          <Row className="mt-6">
            <Col lg={12} className="text-center mb-5">
              <motion.h3 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#ffffff', marginBottom: '1rem', marginTop: '2rem', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                {t.deploiementSupport.support.whyTitle}
              </motion.h3>
              <motion.p style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.9)' }}>
                {t.deploiementSupport.support.whySub}
              </motion.p>
            </Col>
          </Row>

          <Row className="g-4">
            {t.deploiementSupport.support.advantages.map((advantage, index) => (
              <Col lg={3} md={6} key={index}>
                <motion.div initial={{ opacity: 0, y: 50 }} animate={supportInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }} whileHover={{ y: -8, transition: { duration: 0.3 } }} style={{ height: '100%' }}>
                  <Card style={{ border: 'none', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', height: '100%', background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', transition: 'all 0.3s ease' }}>
                    <Card.Body style={{ padding: '2.5rem 2rem', textAlign: 'center' }}>
                      <div style={{ width: '80px', height: '80px', background: `linear-gradient(135deg, ${advantageColors[index]}, ${advantageColors[index]}dd)`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '2.2rem', color: 'white', boxShadow: `0 4px 20px ${advantageColors[index]}33` }}>
                        {advantageIcons[index]}
                      </div>
                      <h4 style={{ color: '#1e293b', fontSize: '1.3rem', fontWeight: 700, marginBottom: '1rem' }}>{advantage.title}</h4>
                      <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6, margin: 0 }}>{advantage.desc}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section ref={setCtaRef} style={{ padding: '6rem 0', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <Container>
          <Row>
            <Col lg={12} className="text-center">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={ctaInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}
                style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: '24px', padding: '4rem 3rem', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.15)' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")', opacity: 0.4 }} />
                <div style={{ background: 'linear-gradient(135deg, rgba(217,0,0,0.2), rgba(217,0,0,0.1))', borderRadius: '20px', width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem auto', position: 'relative', zIndex: 1, border: '2px solid rgba(217,0,0,0.3)' }}>
                  <FaHandsHelping style={{ fontSize: '2.2rem', color: '#d90000', filter: 'drop-shadow(0 2px 8px rgba(217,0,0,0.3))' }} />
                </div>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', fontSize: '2.5rem', color: '#ffffff', lineHeight: '1.2', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                    {t.deploiementSupport.cta.title}
                  </h3>
                  <p style={{ fontSize: '1.2rem', marginBottom: '2.5rem', opacity: 0.9, maxWidth: '700px', margin: '0 auto 2.5rem auto', lineHeight: '1.6', color: 'rgba(255,255,255,0.9)' }}>
                    {t.deploiementSupport.cta.desc}
                  </p>
                  <Button size="lg" onClick={() => navigate('/#contact')}
                    style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600, fontSize: '1rem', background: 'linear-gradient(135deg, #ffffff, #f8fafc)', color: '#1e293b', border: 'none', boxShadow: '0 4px 16px rgba(255,255,255,0.2)' }}>
                    <FaPhoneAlt className="me-2" /> {t.deploiementSupport.cta.btn}
                  </Button>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default DeploiementSupport;